import 'bootstrap/dist/css/bootstrap.min.css';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { Form } from 'react-bootstrap';
import { CheckCircle, ExclamationTriangleFill, Plus, PlusSquare, Trash } from 'react-bootstrap-icons';
import Button from '../../components/Button';

//styles
import { getFromSessionStorage } from '../../constants/functions';
import { useUserContext } from '../../context/UserContext';
import { getAddressByCep, saveAddressInformation } from '../../services/changeAddress';
import {
  AlertContent,
  ButtonGroup,
  ErrorContent,
  FormGroup,
  GroupsContainer,
  InputsGroup,
  PageTitleContainer,
  RemoveMobile,
  Title,
  WarningContent,
} from './styles';

const ChangeAddress = () => {
  const [phones, setPhones] = useState<{ type: string; value: string }[]>([{ type: '', value: '' }]);
  const [originalPhones, setOriginalPhones] = useState<string[]>([]);
  const [emails, setEmails] = useState<{ type: string; value: string }[]>([{ type: '', value: '' }]);
  const [showAlert, setShowAlert] = useState<boolean>();
  const [cep, setCep] = useState('');
  const [endereco, setEndereco] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');
  const [permitirAlterar, setPermitirAlterar] = useState(true);
  const [rendaFaturamento, setRendaFaturamento] = useState('');
  const [selectedEndereco, setSelectedEndereco] = useState('001');
  const { userData, getUserData } = useUserContext();

  //a API pode devolver o cadastro sem a lista de enderecos
  const addressList = useMemo(() => userData?.addresses ?? [], [userData]);
  //addNewAddress altera a lista no lugar, entao precisa forcar o redesenho
  const [, refreshAddressList] = useReducer((x) => x + 1, 0);

  function addNewAddress() {
    if (!userData) return;
    setCep('');
    setEndereco('');
    setNumero('');
    setComplemento('');
    setBairro('');
    setRendaFaturamento('');
    if (!userData.addresses) userData.addresses = addressList;
    userData.addresses.push({
      '0': '',
      '1': '',
      '15': '',
      '17': '',
      '18': '',
      '19': '',
      '2': '',
      '3': '',
      Observação: '',
      active: '',
      checked: false,
      city: '',
      complement: '',
      firstZipCode: '',
      id: (addressList.length + 1).toString().padStart(3, '0'),
      main: '',
      neighborhood: '',
      number: '',
      secondZipCode: '',
      state: '',
      stateDescription: '',
      street: '',
      zipCode: '',
    });
    setSelectedEndereco(addressList.length.toString().padStart(3, '0'));
    refreshAddressList();
  }

  useEffect(() => {
    if (!userData) return;
    const selectedAdd = addressList.find((address) => Number(selectedEndereco) === Number(address.id));
    if (selectedAdd) {
      setBairro(selectedAdd.neighborhood);
      setEndereco(selectedAdd.street);
      setNumero(selectedAdd.number);
      setCep(selectedAdd.zipCode);
      setCidade(selectedAdd.city);
      setEstado(selectedAdd.state);
      setComplemento(selectedAdd.complement);
    }

    setRendaFaturamento(userData?.txt_vlrenda + '');
    setPermitirAlterar(userData?.permite_alterar === 'N' ? false : true);

    const cleanPhones = (userData?.phones ?? []).map((p: any) => p.telefoneCompleto?.replace(/\s+/g, '') || '');
    setPhones(
      cleanPhones.length ? cleanPhones.map((v: string) => ({ type: '', value: v })) : [{ type: '', value: '' }]
    );
    setOriginalPhones(cleanPhones);

    const cadastrados = (userData?.emails ?? []).map((email: any) => ({
      type: '',
      value: email.endereco,
    }));
    setEmails(cadastrados.length ? cadastrados : [{ type: '', value: '' }]);
  }, [userData, addressList, selectedEndereco]);

  const normalizeUF = (s: string) =>
    (s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .replace(/[^A-Z]/g, '')
      .slice(0, 2);

  const handleSalvar = async () => {
    if (!permitirAlterar) return;
    if (userData) {
      const addresses = addressList.map((add) => ({
        checked: false,
        zipCode: add.zipCode || '',
        street: add.street || '',
        number: (add.number || '').toString().replace(/\D/g, '').padStart(6, '0'),
        complement: add.complement || '',
        neighborhood: add.neighborhood || '',
        city: add.city || '',
        state: normalizeUF(add.state || add.stateDescription || ''),
      }));

      const changedAddressIndex = addressList.findIndex((add) => Number(add.id) === Number(selectedEndereco));

      if (changedAddressIndex < 0) {
        console.warn('Nenhum endereço selecionado para salvar');
        return;
      }

      addresses[changedAddressIndex] = {
        ...addresses[changedAddressIndex],
        zipCode: cep,
        complement: complemento,
        neighborhood: bairro,
        number: (numero || '').toString().replace(/\D/g, '').padStart(6, '0'),
        street: endereco,
        city: cidade,
        state: normalizeUF(estado),
      };

      addresses[0].checked = true;

      const formattedPhones = phones.map((p) => p.value.replace(/\s+/g, '').trim()).filter((v) => v !== '');

      const payload = {
        type: userData.txt_pessoa,
        nome: userData.nome,
        nomeEmpresa: userData.nome,
        renda: userData.txt_pessoa === '1' ? rendaFaturamento : '',
        faturamento: userData.txt_pessoa === '1' ? '' : rendaFaturamento,
        sexo: userData.sexo,
        estadoCivil: userData.txt_estado_civil,
        nascimento: userData.txt_dt_nasc_res,
        rg: userData.txt_rg,
        informa_paises: userData.txt_informa_paises,
        pais_endereco: userData.txt_pais_endereco,
        pais_resid_fiscal: userData.txt_pais_resid_fiscal,
        pais_nacionalidade: userData.txt_pais_nacionalidade,
        tpdeclarado: userData.txt_tpdeclarado,
        nacionalidade: userData.txt_nacionalidade,
        naturalidade: userData.txt_naturalidade,
        txt_naturalidade_uf: userData.txt_naturalidade_uf,
        pais_1: userData.txt_pais_1,
        NIF_1: userData.txt_NIF_1,
        pais_2: userData.txt_pais1_2,
        NIF_2: userData.txt_NIF1_2,
        pais_3: userData.txt_pais1_3,
        NIF_3: userData.txt_NIF1_3,
        orgaoEmissor: userData.txt_orgaoEmissor,
        informa_paises1: userData.txt_informa_paises1,
        pais_endereco1: userData.txt_pais_endereco1,
        pais_resid_fiscal1: userData.txt_pais_resid_fiscal1,
        pais_nacionalidade1: userData.txt_pais_nacionalidade1,
        tpdeclarado1: userData.txt_tpdeclarado1,
        pais1_1: userData.txt_pais1_1,
        NIF1_1: userData.txt_NIF1_1,
        pais1_2: userData.txt_pais1_2,
        emails: emails.map((email) => email.value).filter((e) => e !== ''),
        phones: formattedPhones,
      };

      try {
        const res = await saveAddressInformation({
          cpfCnpj: getFromSessionStorage('cpfCnpj'),
          person: payload,
          addresses,
        });

        await getUserData();
        if (res && res.status) {
          setShowAlert(true);
          setTimeout(() => setShowAlert(false), 5000);
        }
      } catch (e) {
        console.error('Erro ao salvar endereço:', e);
      }
    }
  };

  const handleChangeCEP = async (value: string) => {
    const numberRegex = /^[0-9]+$/;
    if (value.length < cep.length) return setCep(value);
    if (numberRegex.test(value) || value === '') {
      if (value.length >= 8) {
        try {
          const res = await getAddressByCep(value);
          if (res) {
            setBairro(res.bairro);
            setEndereco(res.rua);
            setCep(res.cep);
            setEstado(res.estado);
            setCidade(res.cidade);
          }
          return;
        } catch (e) {
          console.log(e);
        }
      }
      return setCep(value);
    }
  };

  const handleRendaMask = (e: string) => {
    if (['', ' '].includes(e)) e = '0,00';
    e = e.replace('.', '').replace(',', '').replace(/\D/g, '');
    const result = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
    }).format(parseFloat(e) / 100);
    setRendaFaturamento(result);
  };

  const pageContent = () => (
    <>
      <form>
        <PageTitleContainer>
          <div>
            <Title>Alteração de Endereço</Title>
            <Form.Select
              onChange={(e) => setSelectedEndereco(e.target.value)}
              value={selectedEndereco}
              disabled={!permitirAlterar}
            >
              {addressList.map((address) => (
                <option key={address.id} value={address.id}>
                  {address.street}, {address.number} - {address.complement}, {address.neighborhood}, {address.zipCode}
                </option>
              ))}
            </Form.Select>
          </div>
          <Button type="reset" onClick={addNewAddress} disabled={!permitirAlterar}>
            <PlusSquare /> Adicionar novo endereço
          </Button>
        </PageTitleContainer>

        {!permitirAlterar && (
          <WarningContent>
            <ExclamationTriangleFill size={22} />
            Consorciado com cota contemplada: Atualização cadastral não autorizada nesta cota. Entre em contato com a
            Administradora.
          </WarningContent>
        )}

        {showAlert && (
          <AlertContent>
            <CheckCircle size={22} />
            Alterações feita com sucesso!
          </AlertContent>
        )}

        <div className="row">
          <div className="col-md-6">
            <FormGroup controlId="formBasicTYPE">
              <Form.Label>Tipo</Form.Label>
              <Form.Select disabled={!permitirAlterar}>
                <option value="">Padrão</option>
                {/*<option value="">option 2</option>*/}
                {/*<option value="">option 3</option>*/}
              </Form.Select>
            </FormGroup>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <FormGroup controlId="formBasicCEP">
              <Form.Label>CEP</Form.Label>
              <Form.Control
                type="text"
                placeholder="CEP"
                value={cep}
                onChange={(e: any) => {
                  handleChangeCEP(e.target.value);
                }}
                disabled={!permitirAlterar}
                required
              />
            </FormGroup>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <FormGroup controlId="streetAddress">
              <Form.Label>Endereço</Form.Label>
              <Form.Control
                type="text"
                placeholder="Endereço"
                value={endereco}
                onChange={(e: any) => setEndereco(e.target.value)}
                disabled={!permitirAlterar}
                required
              />
            </FormGroup>
          </div>

          <div className="col-sm-6">
            <FormGroup controlId="numberAddress">
              <Form.Label>Número</Form.Label>
              <Form.Control
                type="text"
                placeholder="Número"
                value={numero}
                onChange={(e: any) => setNumero(e.target.value)}
                disabled={!permitirAlterar}
                required
              />
            </FormGroup>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <FormGroup controlId="complementAddress">
              <Form.Label>Complemento</Form.Label>
              <Form.Control
                type="text"
                placeholder="Complemento"
                value={complemento}
                onChange={(e: any) => setComplemento(e.target.value)}
                disabled={!permitirAlterar}
              />
            </FormGroup>
          </div>

          <div className="col-sm-6">
            <FormGroup controlId="districtAddress">
              <Form.Label>Bairro</Form.Label>
              <Form.Control
                type="text"
                placeholder="Bairro"
                value={bairro}
                onChange={(e: any) => setBairro(e.target.value)}
                disabled={!permitirAlterar}
                required
              />
            </FormGroup>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <FormGroup controlId="cityAddress">
              <Form.Label>Cidade</Form.Label>
              <Form.Select disabled>
                <option value={cidade}>{cidade}</option>
              </Form.Select>
            </FormGroup>
          </div>

          <div className="col-sm-6">
            <FormGroup controlId="stateAddress">
              <Form.Label>Estado</Form.Label>
              <Form.Select disabled>
                <option value={estado}>{estado}</option>
              </Form.Select>
            </FormGroup>
          </div>
        </div>

        <GroupsContainer>
          <div className="row">
            <div className="col-lg-9 col-xl-6">
              <span>Telefone</span>

              {phones.map((item, index) => (
                <InputsGroup key={index}>
                  <Form.Select
                    onChange={(e) =>
                      setPhones((prev) => prev.map((p, i) => (i === index ? { ...p, type: e.target.value } : p)))
                    }
                    value={item.type}
                    disabled={!permitirAlterar}
                  >
                    <option value="default">Padrão</option>
                  </Form.Select>

                  <Form.Control
                    type="text"
                    placeholder="DDD+Telefone"
                    value={item.value}
                    onChange={(e) =>
                      setPhones((prev) => prev.map((p, i) => (i === index ? { ...p, value: e.target.value } : p)))
                    }
                    disabled={!permitirAlterar}
                  />

                  <div className="d-none d-md-block">
                    {index === 0 ? (
                      <Button
                        className="roundButton icon"
                        type="button"
                        onClick={() => setPhones([...phones, { type: '', value: '' }])}
                        disabled={!permitirAlterar}
                      >
                        <Plus size={22} />
                      </Button>
                    ) : (
                      <RemoveMobile
                        type="button"
                        onClick={() => setPhones((prev) => prev.filter((_, i) => i !== index))}
                        disabled={!permitirAlterar}
                      >
                        <Trash size={22} color="#DC3545" />
                      </RemoveMobile>
                    )}
                  </div>
                </InputsGroup>
              ))}
            </div>
          </div>

          <div className="row">
            <div className="col-lg-9 col-xl-6">
              <span>E-mail</span>

              {emails.map((item, index) => (
                <InputsGroup key={index}>
                  <Form.Select
                    onChange={(e) =>
                      setEmails((prev) => prev.map((p, i) => (i === index ? { ...p, type: e.target.value } : p)))
                    }
                    value={item.type}
                    disabled={!permitirAlterar}
                  >
                    <option value="default">Padrão</option>
                  </Form.Select>

                  <Form.Control
                    type="email"
                    placeholder="Email"
                    value={item.value}
                    onChange={(e) =>
                      setEmails((prev) => prev.map((p, i) => (i === index ? { ...p, value: e.target.value } : p)))
                    }
                    disabled={!permitirAlterar}
                  />

                  <div className="d-none d-md-block">
                    {index === 0 ? (
                      <Button
                        className="roundButton icon"
                        onClick={() => setEmails([...emails, { type: '', value: '' }])}
                        disabled={!permitirAlterar}
                      >
                        <Plus size={22} />
                      </Button>
                    ) : (
                      <RemoveMobile
                        type="button"
                        onClick={() => setEmails((prev) => prev.filter((_, i) => i !== index))}
                        disabled={!permitirAlterar}
                      >
                        <Trash size={22} color="#DC3545" />
                      </RemoveMobile>
                    )}
                  </div>
                </InputsGroup>
              ))}

              <div className="d-flex d-md-none align-items-center justify-content-end w-100 gap-4">
                <Button
                  className="roundButton"
                  type="button"
                  onClick={() => setEmails([...emails, { type: '', value: '' }])}
                  disabled={!permitirAlterar}
                >
                  <Plus size={22} />
                  Adicionar novo Email
                </Button>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-5">
              <FormGroup className="m-0" controlId="formBasicBILLING">
                <Form.Label>Renda/Faturamento</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Renda/Faturamento"
                  required
                  value={rendaFaturamento}
                  onChange={(e: any) => {
                    handleRendaMask(e.target.value);
                  }}
                  disabled={!permitirAlterar}
                />
              </FormGroup>
            </div>
          </div>
        </GroupsContainer>

        <ButtonGroup>
          <Button className="reset" type="reset" onClick={() => window.location.reload()}>
            Restaurar
          </Button>
          <Button className="submit" onClick={() => handleSalvar()} disabled={!permitirAlterar}>
            Salvar
          </Button>
        </ButtonGroup>
      </form>
    </>
  );

  //sem endereco carregado o formulario nao pode ser usado: o salvar reenvia a lista
  //inteira e sobrescreveria o cadastro do consorciado
  const addressLoadError = () => (
    <>
      <PageTitleContainer>
        <div>
          <Title>Alteração de Endereço</Title>
        </div>
      </PageTitleContainer>
      <ErrorContent>
        <ExclamationTriangleFill size={22} />
        Erro no carregamento do endereço. Contate a Administradora.
      </ErrorContent>
    </>
  );

  return <>{userData && <div>{addressList.length ? pageContent() : addressLoadError()}</div>}</>;
};

export default ChangeAddress;
